/**
 * RAG Connector Service
 *
 * Handles communication with the ai-hybridRAG-server via OpenAI-compatible endpoints.
 * Supports SSE streaming for real-time chat responses.
 */

import type { RAGSource, SSESourcesEvent, SSEChunkEvent } from '@/types/source'
import type { PublicSuggestedQuestionsResponse } from '@/types/suggestedQuestions'
import { normalizeRagUrl } from '@/utils/ragUrl'
import type { ReplyWorkflow } from '@/utils/workflowReplyText'
import { useAuthStore } from '@/stores/authStore'
import { useAppStore } from '@/stores/appStore'

// Types matching OpenAI API format
export interface ChatMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

export interface ChatCompletionRequest {
  model?: string
  messages: ChatMessage[]
  temperature?: number
  max_tokens?: number
  stream?: boolean
  // RAG-specific parameters
  top_k?: number
  score_threshold?: number
}

export interface ChatCompletionChoice {
  index: number
  message: ChatMessage
  finish_reason: 'stop' | 'length' | 'content_filter' | null
}

export interface ChatCompletionResponse {
  id: string
  object: 'chat.completion'
  created: number
  model: string
  choices: ChatCompletionChoice[]
  usage?: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
  }
  // RAG-specific: sources from knowledge base
  sources?: RAGSource[]
}

/** Why a reply was cut short (SA-1241). */
export type CutReason = 'length' | 'content_filter'

/** A reply cut at the token limit or by the provider's filter (SA-1241). */
export interface ReplyCut {
  reason: CutReason
  continuable: boolean
}

/**
 * How a chunk carrying a finish reason ends the reply (SA-1241): `length` and
 * `content_filter` are cuts, anything else is not. Only a `length` cut may be
 * continued, and not when the chunk says `continuable: false` (a workflow reply).
 */
function endCut(finishReason: string, chunk: { continuable?: unknown }): ReplyCut | null {
  if (finishReason !== 'length' && finishReason !== 'content_filter') return null
  return { reason: finishReason, continuable: finishReason === 'length' && chunk.continuable !== false }
}

// Re-export SSE types for convenience (actual definitions in types/source.ts)
export type { SSESourcesEvent, SSEChunkEvent }

/**
 * Union type for SSE events that can be received from the RAG server.
 * A mid-stream server failure arrives in-band on the committed 200 as a raw
 * `{"error": {...}}` frame (no `object`) followed by a chunk with
 * `finish_reason: "error"`, then `[DONE]` — see ai-hybridRAG-server CLAUDE.md
 * (streaming section).
 */
type SSEEvent = (SSESourcesEvent | SSEChunkEvent | { object?: string }) & {
  error?: { message?: string; type?: string }
  /** On the final chunk of a reply that cannot be continued (SA-1241). */
  continuable?: boolean
}

// Note: <think>...</think> filtering is handled in ChatContainer's onToken
// handler (thinkingStateRef) rather than here, to keep raw content available
// for backend sync while filtering only the display/TTS path.

export interface ApprovalRequiredEvent {
  approval_id: string
  tool_call_id: string | null
  capability_id: string
}

export interface ToolCallDelta {
  index: number
  id?: string
  function?: { name?: string; arguments?: string }
  type?: string
}

export interface ToolResultEvent {
  role: 'tool'
  tool_call_id: string
  content: string
  [k: string]: unknown
}

/** The workflow a `chat.completion.workflow` frame names, or null when it names none. */
function readReplyWorkflow(value: unknown): ReplyWorkflow | null {
  if (!value || typeof value !== 'object') return null
  const { id, name } = value as { id?: unknown; name?: unknown }
  if (typeof id !== 'string' || !id) return null
  return { id, name: typeof name === 'string' ? name : null }
}

export interface StreamCallbacks {
  onToken: (token: string) => void
  onSources?: (sources: RAGSource[]) => void
  /** The stream ended; `end.cut` says when the reply stopped early, else null (SA-1241). */
  onComplete?: (fullContent: string, end?: { cut: ReplyCut | null }) => void
  onError?: (error: Error) => void
  onApprovalRequired?: (e: ApprovalRequiredEvent) => void
  onToolCallDelta?: (deltas: ToolCallDelta[]) => void
  onToolResult?: (e: ToolResultEvent) => void
  /** A workflow, not the persona, answered this turn (SA-474; backend streams only). */
  onWorkflow?: (workflow: ReplyWorkflow) => void
}

export interface RagConnectorConfig {
  baseUrl: string
  timeout?: number
  apiKey?: string
}

export interface ModelInfo {
  id: string
  object: string
  created?: number
  owned_by?: string
}

export interface ModelsResponse {
  object: string
  data: ModelInfo[]
}

/** Per-call options for direct RAG requests (never stored on the connector). */
export interface RagCallOptions {
  /** The persona's graph id, sent as X-Graph-Id when non-empty. */
  graphId?: string | null
}

export class RagConnector {
  private baseUrl: string
  private timeout: number
  private apiKey: string
  private abortController: AbortController | null = null

  /** SA-1034 PR 5 (spec §3.3): the badge reads the stream's real outcome.
   * A completed stream is `connected`; a response that is not ok, a bad body,
   * an error frame or a transport failure is `error`; an abort is nothing. */
  private report(status: 'connected' | 'error'): void {
    useAppStore.getState().setRagConnectionStatus(status)
  }

  /**
   * Normalize a server URL via the shared `normalizeRagUrl` utility.
   *
   * In addition to the trailing-slash and leading-slash housekeeping the
   * old impl did, the utility also rewrites absolute URLs that point at
   * the same Azure Container Apps environment to the same-origin
   * `/rag-proxy/<instance>` form, defending against personas that have a
   * full RAG URL written into `rag_config.server_url`.
   */
  private static normalizeUrl(url: string): string {
    return normalizeRagUrl(url)
  }

  constructor(config: RagConnectorConfig) {
    this.baseUrl = RagConnector.normalizeUrl(config.baseUrl)
    this.timeout = config.timeout ?? 30000
    this.apiKey = config.apiKey ?? ''
  }

  /**
   * Update the base URL (e.g., when settings change)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = RagConnector.normalizeUrl(url)
  }

  /**
   * Update the API key (e.g., when settings or active persona changes)
   */
  setApiKey(apiKey: string): void {
    this.apiKey = apiKey
  }

  /**
   * Build headers including the x-api-key header when an API key is configured
   */
  private getAuthHeaders(apiKeyOverride?: string): Record<string, string> {
    const key = apiKeyOverride ?? this.apiKey
    if (key) {
      return { 'x-api-key': key }
    }
    return {}
  }

  /**
   * The graph header for ONE call. A graph id is per call, never kept on the
   * shared instance: several callers set the base URL and key, and a persona's
   * graph must not leak into the next caller's request (SA-1356).
   */
  private static graphHeader(graphId?: string | null): Record<string, string> {
    const id = typeof graphId === 'string' ? graphId.trim() : ''
    return id ? { 'X-Graph-Id': id } : {}
  }

  /**
   * Test connection to the RAG server
   */
  async testConnection(options?: RagCallOptions): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: { ...this.getAuthHeaders(), ...RagConnector.graphHeader(options?.graphId) },
        signal: AbortSignal.timeout(5000),
      })

      if (response.ok) {
        return { success: true }
      }

      return {
        success: false,
        error: `Server returned ${response.status}: ${response.statusText}`,
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  /**
   * Fetch available models from the RAG server
   */
  async fetchModels(options?: RagCallOptions): Promise<ModelsResponse> {
    const response = await fetch(`${this.baseUrl}/v1/models`, {
      method: 'GET',
      headers: { ...this.getAuthHeaders(), ...RagConnector.graphHeader(options?.graphId) },
      signal: AbortSignal.timeout(5000),
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch models: ${response.status} - ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Fetch available models from a specific server URL (for persona-specific server override)
   */
  async fetchModelsFromUrl(serverUrl: string, apiKey?: string): Promise<ModelsResponse> {
    const url = RagConnector.normalizeUrl(serverUrl)
    const response = await fetch(`${url}/v1/models`, {
      method: 'GET',
      headers: this.getAuthHeaders(apiKey),
      signal: AbortSignal.timeout(5000),
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch models: ${response.status} - ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Fetch suggested questions from the RAG server's public endpoint.
   *
   * Hits `/api/suggested-questions` (QUERY scope). When `graphId` is set,
   * forwards it as `X-Graph-Id` so per-graph configurations are honored;
   * otherwise the server falls back to `DEFAULT_GRAPH_ID`.
   */
  async fetchSuggestedQuestions(
    serverUrl?: string,
    apiKey?: string,
    graphId?: string,
  ): Promise<string[]> {
    const url = (serverUrl ? RagConnector.normalizeUrl(serverUrl) : this.baseUrl)
    const headers: Record<string, string> = {
      ...this.getAuthHeaders(apiKey),
      ...RagConnector.graphHeader(graphId),
    }
    try {
      const response = await fetch(`${url}/api/suggested-questions`, {
        method: 'GET',
        headers,
        signal: AbortSignal.timeout(5000),
      })

      if (!response.ok) {
        console.warn('[RagConnector] Failed to fetch suggested questions:', response.status)
        return []
      }

      const data: PublicSuggestedQuestionsResponse = await response.json()
      return data.questions ?? []
    } catch (error) {
      console.warn('[RagConnector] Error fetching suggested questions:', error)
      return []
    }
  }

  /**
   * Send a chat completion request (non-streaming)
   */
  async chatCompletion(request: ChatCompletionRequest): Promise<ChatCompletionResponse> {
    const response = await fetch(`${this.baseUrl}/v1/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
      body: JSON.stringify({
        ...request,
        stream: false,
      }),
      signal: AbortSignal.timeout(this.timeout),
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Chat completion failed: ${response.status} - ${error}`)
    }

    return response.json()
  }

  /**
   * Send a streaming chat completion request
   * Uses SSE (Server-Sent Events) for real-time token streaming
   */
  async streamChatCompletion(
    request: ChatCompletionRequest,
    callbacks: StreamCallbacks,
    options?: RagCallOptions,
  ): Promise<void> {
    // Cancel any existing stream
    this.cancelStream()

    // This stream's own handle: its end clears only this one, never a newer
    // stream's (SA-1399).
    const controller = new AbortController()
    this.abortController = controller

    const authHeaders = this.getAuthHeaders()
    if (!authHeaders['x-api-key']) {
      console.warn('[RagConnector] No API key set — request will be unauthenticated. Configure a key in Settings or per-persona.')
    }

    try {
      const response = await fetch(`${this.baseUrl}/v1/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...authHeaders,
          ...RagConnector.graphHeader(options?.graphId),
        },
        body: JSON.stringify({
          ...request,
          stream: true,
        }),
        signal: controller.signal,
      })

      if (!response.ok) {
        const error = await response.text()
        throw new Error(`Stream failed: ${response.status} - ${error}`)
      }

      if (!response.body) {
        throw new Error('Response body is null')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      // Note: ThinkingTagFilter is NOT used here — filtering is done in
      // ChatContainer's onToken handler to avoid double-filtering issues.
      let fullContent = ''
      let buffer = ''
      let completed = false
      let cut: ReplyCut | null = null

      try {
        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            break
          }

          buffer += decoder.decode(value, { stream: true })

          // Process SSE lines
          const lines = buffer.split('\n')
          buffer = lines.pop() || '' // Keep incomplete line in buffer

          for (const line of lines) {
            const trimmedLine = line.trim()

            if (!trimmedLine || trimmedLine.startsWith(':')) {
              // Empty line or comment, skip
              continue
            }

            if (trimmedLine.startsWith('data: ')) {
              const data = trimmedLine.slice(6)

              if (data === '[DONE]') {
                // Stream complete
                if (!completed) {
                  completed = true
                  this.report('connected')
                  callbacks.onComplete?.(fullContent, { cut })
                }
                return
              }

              let parsed: SSEEvent
              try {
                parsed = JSON.parse(data)
              } catch (parseError) {
                console.warn('Failed to parse SSE chunk:', data, parseError)
                continue
              }

              // Mid-stream server failure arrives in-band on the committed 200
              // (error frame, then a finish_reason "error" chunk). Fail the
              // turn loudly — draining to [DONE] would render a crash as a
              // clean empty answer.
              if (parsed.error) {
                throw new Error(parsed.error.message || 'Stream failed with server error')
              }

              // Handle sources event (chat.completion.sources)
              if (parsed.object === 'chat.completion.sources') {
                const sourcesEvent = parsed as SSESourcesEvent
                if (sourcesEvent.sources && sourcesEvent.sources.length > 0) {
                  callbacks.onSources?.(sourcesEvent.sources)
                }
                continue // Don't process as chunk
              }

              // Handle chunk event (chat.completion.chunk)
              if (parsed.object === 'chat.completion.chunk') {
                const chunk = parsed as SSEChunkEvent
                const finish = chunk.choices?.[0]?.finish_reason
                if (finish === 'error') {
                  throw new Error('Stream failed with server error')
                }
                // The reply's last finish reason says whether it was cut (SA-1241).
                if (finish) cut = endCut(finish, parsed)
                const delta = chunk.choices?.[0]?.delta
                if (delta?.content) {
                  fullContent += delta.content
                  callbacks.onToken(delta.content)
                }

                // Note: finish_reason 'stop' may arrive before sources event.
                // Don't call onComplete here — wait for [DONE] which comes after
                // all events (including sources) have been sent.
                continue
              }

              // Unknown event type - log for debugging
              console.debug('Unknown SSE event type:', parsed.object)
            }
          }
        }

        // If we exit the loop without [DONE], still call complete
        if (!completed) {
          completed = true
          this.report('connected')
          callbacks.onComplete?.(fullContent, { cut })
        }
      } finally {
        // Release the connection on every exit — error-terminal throw, [DONE]
        // return, or normal completion. A crashed server may never close the
        // stream, so don't rely on it draining.
        reader.cancel().catch(() => {})
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Stream was cancelled, not an error
        return
      }
      this.report('error')
      callbacks.onError?.(error instanceof Error ? error : new Error('Unknown streaming error'))
    } finally {
      if (this.abortController === controller) this.abortController = null
    }
  }

  /**
   * Stream a chat completion via the backend `/api/chat/completions`
   * endpoint (P2.T0 — server-canonical chat path). The browser POSTs
   * `{persona_id, conversation_id, message, history, ...}` and the
   * backend runs `compose()` server-side, opens an httpx async-stream
   * to the persona's resolved RAG URL, and proxies SSE bytes back.
   *
   * SSE parsing is byte-faithful with the existing legacy `/v1/chat/completions`
   * path — the same `data: {...}` events arrive (chunk, sources, [DONE])
   * so the parser below is identical to `streamChatCompletion`'s.
   *
   * Auth: JWT via `Authorization: Bearer <token>` from the auth store.
   * On 401 the auth store's silent-refresh path can't easily run mid-
   * stream; the caller's `onError` fires and the user can retry.
   *
   * Used by the persona-level render path. The direct-to-RAG path stays
   * for personaless chat / embedded widget / third-party callers (Jaso
   * integration) — see `streamChatCompletion`.
   */
  async streamChatViaBackend(
    body: {
      personaId: string
      conversationId?: string | null
      message: string
      history?: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>
      modelOverride?: string
      temperature?: number
      maxTokens?: number
      topK?: number
      scoreThreshold?: number
      voiceActive?: boolean
    },
    callbacks: StreamCallbacks
  ): Promise<void> {
    this.cancelStream()
    const controller = new AbortController()
    this.abortController = controller

    const token = useAuthStore.getState().accessToken
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const payload = {
      persona_id: body.personaId,
      conversation_id: body.conversationId ?? undefined,
      message: body.message,
      history: body.history,
      model_override: body.modelOverride,
      temperature: body.temperature,
      max_tokens: body.maxTokens,
      top_k: body.topK,
      score_threshold: body.scoreThreshold,
      voice_active: body.voiceActive ?? false,
    }

    try {
      const response = await fetch('/api/chat/completions', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        credentials: 'include',
        signal: controller.signal,
      })

      if (!response.ok) {
        const errText = await response.text()
        throw new Error(`Chat failed: ${response.status} - ${errText}`)
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let fullContent = ''
      let buffer = ''
      let completed = false
      let cut: ReplyCut | null = null
      let currentEvent: string | null = null

      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            const trimmedLine = line.trim()

            if (trimmedLine.startsWith(':')) continue

            if (trimmedLine === '') {
              currentEvent = null
              continue
            }

            if (trimmedLine.startsWith('event: ')) {
              currentEvent = trimmedLine.slice(7).trim()
              continue
            }

            if (trimmedLine.startsWith('data: ')) {
              const data = trimmedLine.slice(6)
              if (data === '[DONE]') {
                if (!completed) {
                  completed = true
                  this.report('connected')
                  callbacks.onComplete?.(fullContent, { cut })
                }
                return
              }
              if (currentEvent === 'approval_required') {
                try {
                  const parsed = JSON.parse(data) as ApprovalRequiredEvent
                  callbacks.onApprovalRequired?.(parsed)
                } catch (parseError) {
                  console.warn('Failed to parse approval_required event:', data, parseError)
                }
                currentEvent = null
                continue
              }
              if (currentEvent === 'tool_result') {
                try {
                  const parsed = JSON.parse(data) as ToolResultEvent
                  callbacks.onToolResult?.(parsed)
                } catch (parseError) {
                  console.warn('Failed to parse tool_result event:', data, parseError)
                }
                currentEvent = null
                continue
              }
              if (currentEvent === 'error') {
                try {
                  const parsed = JSON.parse(data) as { error?: string }
                  callbacks.onError?.(new Error(typeof parsed?.error === 'string' ? parsed.error : 'tool framework error'))
                } catch {
                  callbacks.onError?.(new Error('tool framework error'))
                }
                currentEvent = null
                continue
              }
              let parsed: SSEEvent
              try {
                parsed = JSON.parse(data)
              } catch (parseError) {
                console.warn('Failed to parse SSE chunk:', data, parseError)
                currentEvent = null
                continue
              }
              // The backend proxies hybridRAG SSE bytes verbatim, so a
              // mid-stream server failure arrives here too as an in-band
              // error frame / finish_reason "error" chunk. Fail the turn
              // loudly instead of draining to [DONE].
              if (parsed.error) {
                throw new Error(parsed.error.message || 'Stream failed with server error')
              }
              if (parsed.object === 'chat.completion.sources') {
                const ev = parsed as SSESourcesEvent
                if (ev.sources?.length) callbacks.onSources?.(ev.sources)
                currentEvent = null
                continue
              }
              if (parsed.object === 'chat.completion.workflow') {
                const workflow = readReplyWorkflow((parsed as { workflow?: unknown }).workflow)
                if (workflow) callbacks.onWorkflow?.(workflow)
                currentEvent = null
                continue
              }
              if (parsed.object === 'chat.completion.chunk') {
                const chunk = parsed as SSEChunkEvent
                const finish = chunk.choices?.[0]?.finish_reason
                if (finish === 'error') {
                  throw new Error('Stream failed with server error')
                }
                // The reply's last finish reason says whether it was cut (SA-1241).
                if (finish) cut = endCut(finish, parsed)
                const delta = chunk.choices?.[0]?.delta
                if (delta?.content) {
                  fullContent += delta.content
                  callbacks.onToken(delta.content)
                }
                if (delta && 'tool_calls' in delta && Array.isArray((delta as Record<string, unknown>).tool_calls)) {
                  callbacks.onToolCallDelta?.((delta as Record<string, unknown>).tool_calls as ToolCallDelta[])
                }
                currentEvent = null
                continue
              }
              console.debug('Unknown SSE event type:', parsed.object)
              currentEvent = null
            }
          }
        }

        if (!completed) {
          completed = true
          this.report('connected')
          callbacks.onComplete?.(fullContent, { cut })
        }
      } finally {
        // Release the connection on every exit — error-terminal throw, [DONE]
        // return, or normal completion.
        reader.cancel().catch(() => {})
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return
      this.report('error')
      callbacks.onError?.(
        error instanceof Error ? error : new Error('Unknown streaming error')
      )
    } finally {
      if (this.abortController === controller) this.abortController = null
    }
  }

  /**
   * Cancel the current streaming request
   */
  cancelStream(): void {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  /**
   * Build messages array with system prompt and conversation history
   */
  buildMessages(
    systemPrompt: string,
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>,
    userMessage: string
  ): ChatMessage[] {
    return [
      { role: 'system', content: systemPrompt },
      ...conversationHistory,
      { role: 'user', content: userMessage },
    ]
  }
}

// Singleton instance
let ragConnectorInstance: RagConnector | null = null

export function getRagConnector(): RagConnector {
  if (!ragConnectorInstance) {
    ragConnectorInstance = new RagConnector({
      baseUrl: import.meta.env.VITE_RAG_SERVER_URL || 'http://localhost:8000',
    })
  }
  return ragConnectorInstance
}

export function initRagConnector(config: RagConnectorConfig): RagConnector {
  ragConnectorInstance = new RagConnector(config)
  return ragConnectorInstance
}

// Session connectors (SA-1399): a surface that streams on its own (an Expert
// RAG session) gets a connector of its own, so it never cancels, cuts off or
// re-points the chat's stream on the singleton, nor the chat its. One is live
// from register to release; an identity change clears every live one's key.
const liveSessionConnectors = new Set<RagConnector>()

/** A new connector, never the singleton; not live until registered. */
export function createSessionConnector(): RagConnector {
  return new RagConnector({ baseUrl: '' })
}

/** Count a session connector live, so an identity change clears its key. */
export function registerSessionConnector(connector: RagConnector): void {
  liveSessionConnectors.add(connector)
}

/** Cancel the connector's stream and stop counting it live. Safe to repeat. */
export function releaseSessionConnector(connector: RagConnector): void {
  connector.cancelStream()
  liveSessionConnectors.delete(connector)
}

/** Clear the key of every live session connector (the identity reset). */
export function clearSessionConnectorKeys(): void {
  for (const connector of liveSessionConnectors) connector.setApiKey('')
}
